import { useState } from "react";
import { useTranslation } from "react-i18next";

import { asMarket, asRiskLevel, robotColor, useRobots, type RobotCard } from "@/entities/robot";
import { formatMoney, formatPercent, formatPrice, moneySign, NO_VALUE } from "@/shared/lib";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Сравнение. Figma: `Robots · Compare` (20:128) — таблица из семи строк по три
 * ячейки: название поля и две колонки роботов.
 *
 * Выбора роботов в макете нет — только сама таблица. Чтобы не выдумывать новый
 * элемент управления, колонки переключаются чипами, уже нарисованными в
 * макете (`Bot Chips`, 19:32 на главной). Отступление зафиксировано в
 * DESIGN.md §8.
 *
 * Эндпоинта сравнения в API нет: обе колонки берутся из уже загруженного
 * каталога, второго запроса не делается.
 */
export function RobotsComparePage() {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useRobots();
  const [picked, setPicked] = useState<[number, number]>([0, 1]);

  if (isPending) return <ScreenSkeleton blocks={[60, 336, 72]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  if (data.length < 2) {
    return <ScreenState tone="info" icon="robotFill" title={t("robots.empty.title")} body={t("robots.empty.body")} />;
  }

  // Ставка нулевая — в таблице честнее слово «нет», чем «0,00%»: колонки
  // сравниваются глазами, и ноль среди процентов читается как «дёшево», а не
  // как «комиссии нет вовсе».
  const fee = (robot: RobotCard) =>
    moneySign(robot.profit_fee_pct) > 0
      ? formatPercent(robot.profit_fee_pct)
      : t("buy.terms.feeNone");

  // Прочерк, а не ноль: у робота без покупателей нулевая прибыль формально
  // верна и при этом сообщает не то (ТЗ №3).
  const headcount = (robot: RobotCard) => {
    const value = robot.social_stats.active_users_count;
    return value === null ? NO_VALUE : String(value);
  };
  const realized = (robot: RobotCard) => {
    const value = robot.social_stats.total_realized_user_pnl;
    return value === null ? NO_VALUE : formatMoney(value, { sign: "always" });
  };

  const left = data[picked[0]] ?? data[0];
  const right = data[picked[1]] ?? data[1];
  if (!left || !right) return null;

  const rows: { label: string; left: string; right: string }[] = [
    { label: t("robots.fields.market"), left: t(`robots.market.${asMarket(left.market)}`), right: t(`robots.market.${asMarket(right.market)}`) },
    { label: t("robots.fields.risk"), left: t(`robots.risk.${asRiskLevel(left.risk_level)}`), right: t(`robots.risk.${asRiskLevel(right.risk_level)}`) },
    {
      label: t("robots.fields.minAmount"),
      left: formatPrice(left.min_amount),
      right: formatPrice(right.min_amount),
    },
    // Те же посчитанные показатели, что и на карточке каталога: сравнение — это
    // продолжение списка, и расходиться с ним оно не имеет права. Порядок как
    // на карточке: сначала месяц, потом год.
    { label: t("robots.perf.d30"), left: formatPercent(left.performance.d30, { sign: "always" }), right: formatPercent(right.performance.d30, { sign: "always" }) },
    { label: t("robots.perf.m12full"), left: formatPercent(left.performance.m12, { sign: "always" }), right: formatPercent(right.performance.m12, { sign: "always" }) },
    { label: t("robots.maxDd"), left: formatPercent(left.declared_max_dd), right: formatPercent(right.declared_max_dd) },
    // ТЗ §18 просит строку «AI». Число источников, а не их перечисление:
    // одиннадцать подписей в ячейку шириной в треть экрана не помещаются, а
    // сравнивается здесь именно глубина анализа.
    { label: t("robots.fields.ai"), left: String(left.ai_signals.length), right: String(right.ai_signals.length) },
    { label: t("robots.fields.fee"), left: fee(left), right: fee(right) },
    { label: t("robots.fields.assets"), left: left.assets.join(" · "), right: right.assets.join(" · ") },
    // Те же социальные цифры, что в каталоге и на экране робота: витринные
    // поля оператора здесь больше не читаются.
    { label: t("robots.fields.users"), left: headcount(left), right: headcount(right) },
    { label: t("robots.fields.profit"), left: realized(left), right: realized(right) },
  ];

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("robots.compare")} subtitle={t("robots.compareSub")} />

      {[0, 1].map((column) => (
        <ul key={column} className="flex flex-wrap gap-[7px]">
          {data.map((robot, index) => (
            <li key={robot.id}>
              <ColumnChip
                robot={robot}
                selected={picked[column as 0 | 1] === index}
                onSelect={() => {
                  setPicked((current) =>
                    column === 0 ? [index, current[1]] : [current[0], index],
                  );
                }}
              />
            </li>
          ))}
        </ul>
      ))}

      <table className="w-full rounded-[20px] border border-border-subtle bg-background-elevated text-caption">
        <thead>
          <tr>
            <th className="p-3 text-left font-medium text-text-tertiary">{t("robots.fields.field")}</th>
            <th className="p-3 text-left font-semibold text-text-primary">{left.name}</th>
            <th className="p-3 text-left font-semibold text-text-primary">{right.name}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-t border-border-subtle">
              <td className="p-3 font-medium text-text-secondary">{row.label}</td>
              <td className="p-3 font-semibold text-text-primary">{row.left}</td>
              <td className="p-3 font-semibold text-text-primary">{row.right}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="text-caption font-normal text-text-secondary">{t("robots.compareNote")}</p>
    </div>
  );
}

function ColumnChip({
  robot,
  selected,
  onSelect,
}: {
  robot: RobotCard;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      data-haptic="select"
      onClick={onSelect}
      className={[
        "press flex items-center gap-1.5 rounded-full p-[9px] text-micro font-semibold",
        "transition-colors duration-[var(--motion-fast)] ease-standard",
        selected ? "bg-surface-selected text-text-primary" : "bg-background-raised text-neutral-label",
      ].join(" ")}
    >
      <span
        className="size-[7px] rounded-full"
        style={{ backgroundColor: robotColor(robot) }}
        aria-hidden="true"
      />
      {robot.name}
    </button>
  );
}
